import React, { Suspense } from 'react';

import AppsIcon from '@atlaskit/icon/core/apps';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { type RovoLogoProps } from '../../constants/icons';
import { isNounKey, type NounKeys, nouns } from '../../constants/nouns';
import { type ProductKeys } from '../../constants/product-keys';
import { useProductConfigs } from '../../constants/products';
import {
	getForgeConnectorIcon,
	isForgeConnectorProductKey,
} from '../../constants/schemas/3p-config';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../utils/experiments';
import { getFileTypeBrandIcon } from '../icon-for-3p/file-type-brand-icon';

export type ProductIconProps = RovoLogoProps & {
	product?: ProductKeys;
	subtype?: string | null;
	isSelected?: boolean;
	useSearchResultIcon?: boolean;
};

export type ProductOrNounIconProps = RovoLogoProps & {
	productOrNounKey: ProductKeys | NounKeys | undefined;
	isSelected?: boolean;
};

export const ProductIcon = ({
	product,
	subtype,
	size = 'large',
	isSelected = false,
	useSearchResultIcon = false,
	...props
}: ProductIconProps): React.JSX.Element => {
	const products = useProductConfigs();
	if (product) {
		// First check if it's a Forge connector - prioritize dynamic API icons
		if (isForgeConnectorProductKey(product)) {
			const forgeIcon = getForgeConnectorIcon(product);
			if (forgeIcon) {
				// Forge connector icons are RovoLogo functions, not React components
				return forgeIcon({ ...props, size });
			}
		}
		// Fallback to static products map (covers hardcoded Forge connectors and all other products)
		const config = products[product];
		if (config) {
			const Icon = (useSearchResultIcon && config.SearchResultIcon) || config.Icon;
			if (Icon) {
				const legacyIcon = <Icon {...props} size={size} />;
				const BrandIcon =
					subtype && fg('rovo-search-team-eu-26-brand-icon-library')
						? getFileTypeBrandIcon(product, subtype)
						: null;
				return BrandIcon ? (
					<Suspense fallback={legacyIcon}>
						<BrandIcon {...props} label={props.label ?? ''} size={size} />
					</Suspense>
				) : (
					legacyIcon
				);
			}
		}
	}
	// Fallback to AppsIcon
	return (
		<AppsIcon
			label=""
			spacing="spacious"
			color={isSelected ? token('color.icon.selected') : token('color.icon')}
		/>
	);
};

type ProductOrNounOrShowMoreProos = {
	isShowMore?: boolean;
} & ProductOrNounIconProps;

export const IconDecider = ({
	isShowMore,
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounOrShowMoreProos): React.JSX.Element => {
	if (isShowMore) {
		return (
			<ShowMoreHorizontalIcon
				label=""
				size={
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' ? 'small' : 'medium'
				}
				spacing={
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? 'compact'
						: 'spacious'
				}
				color={isSelected ? token('color.icon.selected') : token('color.icon')}
			/>
		);
	}

	return ProductOrNounIcon({
		productOrNounKey,
		size,
		isSelected,
		...props,
	});
};

export const ProductOrNounIcon = ({
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounIconProps): React.JSX.Element => {
	const products = useProductConfigs();
	let Icon;

	if (productOrNounKey) {
		const isNounFilter = isNounKey(productOrNounKey);

		if (isNounFilter) {
			Icon = nouns[productOrNounKey].Icon;
		} else {
			// First check if it's a Forge connector - prioritize dynamic API icons
			if (isForgeConnectorProductKey(productOrNounKey)) {
				const forgeIcon = getForgeConnectorIcon(productOrNounKey);
				if (forgeIcon) {
					// Forge connector icons are RovoLogo functions, not React components
					return forgeIcon({ ...props, size });
				}
			}
			// Fallback to static products map (covers hardcoded Forge connectors and all other products)
			const productConfig = products[productOrNounKey as ProductKeys];
			if (productConfig?.Icon) {
				Icon = productConfig.Icon;
			}
		}

		if (Icon) {
			return <Icon {...props} size={size} />;
		}
	}

	// Fallback to AppsIcon
	return (
		<AppsIcon
			label=""
			size={
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' ? 'small' : 'medium'
			}
			spacing={
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' ? 'compact' : 'spacious'
			}
			color={isSelected ? token('color.icon.selected') : token('color.icon')}
		/>
	);
};
